import { Box, Toolbar } from '@mui/material';
import { useEffect, useState } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { useActiveProject } from '../../../core/contexts/ProjectContext';
import { AIChatProvider, useAIChatContext } from '../../../ai-assistant/context/AIChatContext';
import { AIChatSidebar } from '../../../ai-assistant/components/AIChatSidebar';
import { AIChatResizeHandle } from '../../../ai-assistant/components/AIChatResizeHandle';
import { ToastProvider } from '../Toast/ToastContext';

/**
 * Top-level shell: header + sidebar + content area. The content column is a
 * flex column whose height is locked to the viewport so child pages can opt
 * into `flex: 1` and let big elements (e.g. a DataTable) fill the remaining
 * space instead of bottoming out at a fixed 540 px.
 *
 * `AIChatProvider` is mounted here (not per-page) so the AI Assistant
 * sidebar is a persistent shell element — its open state, width, and
 * active session survive navigating between pages. `ToastProvider` is
 * mounted here for the same reason: one toast host for the whole app.
 *
 * Onboarding (`/onboarding/*`) creates a project, so no project is selected
 * there and nothing exists to browse: the shell keeps one instance (the
 * providers must survive the jump to the new project's page) but drops the
 * sidebar and the assistant panel, and the header goes minimal.
 */
export function AppLayout() {
  return (
    <ToastProvider>
      <AIChatProvider>
        <AppLayoutContent />
      </AIChatProvider>
    </ToastProvider>
  );
}

function AppLayoutContent() {
  const { isOpen: isAssistantOpen } = useAIChatContext();
  const { setActiveProject } = useActiveProject();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isOnboarding = useMatch('/onboarding/*') !== null;

  // The previous selection is not what the founder is about to work on; the
  // wizard selects the new project itself once it exists.
  useEffect(() => {
    if (isOnboarding) setActiveProject(null);
  }, [isOnboarding, setActiveProject]);

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppHeader
        variant={isOnboarding ? 'onboarding' : 'default'}
        onMenuClick={isOnboarding ? undefined : () => setMobileNavOpen(true)}
      />
      {!isOnboarding && <AppSidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          backgroundColor: 'background.default',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Spacer so the content starts below the fixed AppHeader. */}
        <Toolbar />
        {/* Left: routed page content. Right (optional): AI Assistant sidebar. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          <Box
            sx={{
              p: 3,
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            <Outlet />
          </Box>
          {isAssistantOpen && !isOnboarding && (
            <>
              <AIChatResizeHandle />
              <AIChatSidebar />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
